import { CloseOutlined, PlusOutlined, SendOutlined } from "@ant-design/icons";
import { Button, Input, Tooltip } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import useApi from "../utils/useApi";

const POLLING_MS = 1500;
const STATUS_EM_ABERTO = ["pendente", "processando"];
const ERROS_DO_ENVIO = {
  400: "Escreva uma mensagem.",
  404: "Filial não encontrada ou sem acesso.",
  409: "Aguarde a resposta da mensagem anterior.",
  429: "Muitas mensagens em pouco tempo. Aguarde um minuto.",
  503: "O assistente está indisponível no momento.",
};
const ERRO_GENERICO = "Erro ao conectar com o assistente.";

const doServidor = (item) => ({
  id: `${item.papel}-${item.id}`,
  text: item.texto,
  fromMe: item.papel === "user",
  pendingAction: item.pending_action || null,
});

const mensagemDeErro = (error) =>
  error?.response?.data?.error ||
  ERROS_DO_ENVIO[error?.response?.status] ||
  ERRO_GENERICO;

const expirou = (acao) =>
  !acao?.expires_at || new Date(acao.expires_at).getTime() <= Date.now();

export default function ChatWidget() {
  const { getAgentStatus } = useApi();
  const status = useQuery({
    queryKey: ["agent-status"],
    queryFn: getAgentStatus,
    staleTime: 60000,
    refetchInterval: 300000,
  });

  if (status.data?.enabled !== true) return null;
  return <ChatPanel />;
}

function ChatPanel() {
  const { user } = useAuth();
  const {
    sendAgentMessage,
    getAgentMessage,
    getAgentConversation,
    closeAgentConversation,
    confirmAgentAction,
    cancelAgentAction,
  } = useApi();
  const branchId = user?.active_branch_id ?? null;
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [inputText, setInputText] = useState("");
  const [pendingMessageId, setPendingMessageId] = useState(null);
  const [sending, setSending] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const [, setRelogio] = useState(0);
  const bottomRef = useRef(null);

  const conversa = useQuery({
    queryKey: ["agent-conversation", branchId],
    queryFn: () => getAgentConversation(branchId),
    enabled: isOpen && branchId !== null,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const pendente = useQuery({
    queryKey: ["agent-message", pendingMessageId],
    queryFn: () => getAgentMessage(pendingMessageId),
    enabled: pendingMessageId !== null,
    refetchInterval: (query) =>
      STATUS_EM_ABERTO.includes(query.state.data?.status ?? "pendente")
        ? POLLING_MS
        : false,
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: 2,
  });

  useEffect(() => {
    setMessages([]);
    setConversationId(null);
    setPendingMessageId(null);
  }, [branchId]);

  useEffect(() => {
    if (!conversa.data || pendingMessageId !== null) return;
    setConversationId(conversa.data.conversation?.id ?? null);
    setMessages((conversa.data.messages || []).map(doServidor));
    const aberta = (conversa.data.messages || []).find(
      (item) => item.papel === "user" && STATUS_EM_ABERTO.includes(item.status)
    );
    if (aberta) setPendingMessageId(aberta.id);
  }, [conversa.data]);

  useEffect(() => {
    const data = pendente.data;
    if (!data || STATUS_EM_ABERTO.includes(data.status)) return;
    setMessages((prev) => [
      ...prev,
      data.resposta
        ? doServidor({ ...data.resposta, papel: "assistant" })
        : {
            id: `erro-${data.id}`,
            text: ERRO_GENERICO,
            fromMe: false,
            isError: true,
          },
    ]);
    setPendingMessageId(null);
  }, [pendente.data]);

  useEffect(() => {
    if (!pendente.error) return;
    setMessages((prev) => [
      ...prev,
      {
        id: `erro-${Date.now()}`,
        text: ERRO_GENERICO,
        fromMe: false,
        isError: true,
      },
    ]);
    setPendingMessageId(null);
  }, [pendente.error]);

  const temAcaoAberta = messages.some((msg) => msg.pendingAction);
  useEffect(() => {
    if (!temAcaoAberta) return undefined;
    const timer = setInterval(() => setRelogio((valor) => valor + 1), 15000);
    return () => clearInterval(timer);
  }, [temAcaoAberta]);

  const aguardando = pendingMessageId !== null || sending;

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, aguardando]);

  const adicionarErro = (texto) =>
    setMessages((prev) => [
      ...prev,
      { id: `erro-${Date.now()}`, text: texto, fromMe: false, isError: true },
    ]);

  const sendMessage = async () => {
    const text = inputText.trim();
    if (!text || aguardando || branchId === null) return;
    setInputText("");
    setSending(true);
    setMessages((prev) => [
      ...prev.map((msg) => ({ ...msg, pendingAction: null })),
      { id: `local-${Date.now()}`, text, fromMe: true },
    ]);
    try {
      const resp = await sendAgentMessage({ branch_id: branchId, texto: text });
      setConversationId(resp.conversation_id);
      setPendingMessageId(resp.message_id);
    } catch (error) {
      adicionarErro(mensagemDeErro(error));
    } finally {
      setSending(false);
    }
  };

  const decidir = async (acao, confirmar) => {
    setDeciding(acao.id);
    try {
      const resp = confirmar
        ? await confirmAgentAction(acao.id)
        : await cancelAgentAction(acao.id);
      setMessages((prev) => [
        ...prev.map((msg) =>
          msg.pendingAction?.id === acao.id
            ? { ...msg, pendingAction: null }
            : msg
        ),
        {
          id: `acao-${acao.id}-${Date.now()}`,
          text: resp.texto,
          fromMe: false,
        },
      ]);
    } catch (error) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.pendingAction?.id === acao.id
            ? { ...msg, pendingAction: null }
            : msg
        )
      );
      adicionarErro(mensagemDeErro(error));
    } finally {
      setDeciding(null);
    }
  };

  const novaConversa = async () => {
    if (aguardando) return;
    if (conversationId !== null) {
      try {
        await closeAgentConversation(conversationId);
      } catch (error) {
        adicionarErro(mensagemDeErro(error));
        return;
      }
    }
    setMessages([]);
    setConversationId(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const indisponivel = conversa.data?.enabled === false;

  const renderAcao = (acao) => {
    const vencida = expirou(acao);
    return (
      <div
        style={{
          marginTop: 8,
          padding: "8px 10px",
          borderRadius: 8,
          border: "1px dashed #d97706",
          background: "#fffbeb",
          color: "#111827",
        }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Confirma?</div>
        <div>{acao.resumo}</div>
        {vencida ? (
          <div style={{ marginTop: 6, color: "#9ca3af", fontSize: 12 }}>
            A confirmação expirou. Peça de novo se ainda quiser.
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Button
              size="small"
              type="primary"
              loading={deciding === acao.id}
              disabled={deciding !== null}
              onClick={() => decidir(acao, true)}>
              Confirmar
            </Button>
            <Button
              size="small"
              disabled={deciding !== null}
              onClick={() => decidir(acao, false)}>
              Cancelar
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}>
      {isOpen && (
        <div className="ouran-chat-panel">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #f0f0f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: "12px 12px 0 0",
              background: "#1a1a1a",
              color: "#fff",
            }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Ouran AI</span>
            <div style={{ display: "flex", gap: 4 }}>
              <Tooltip title="Nova conversa">
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  size="small"
                  style={{ color: "#fff" }}
                  disabled={aguardando}
                  onClick={novaConversa}
                />
              </Tooltip>
              <Button
                type="text"
                icon={<CloseOutlined />}
                size="small"
                style={{ color: "#fff" }}
                onClick={() => setIsOpen(false)}
              />
            </div>
          </div>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "12px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}>
            {messages.length === 0 && !conversa.isFetching && (
              <div
                style={{
                  textAlign: "center",
                  color: "#9ca3af",
                  fontSize: 13,
                  marginTop: 32,
                }}>
                {branchId === null
                  ? "Selecione uma filial para conversar."
                  : indisponivel
                    ? "O assistente está indisponível no momento."
                    : "Olá! Como posso ajudar?"}
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  alignSelf: msg.fromMe ? "flex-end" : "flex-start",
                  maxWidth: "80%",
                  background: msg.fromMe ? "#1a1a1a" : "#f3f4f6",
                  color: msg.fromMe
                    ? "#fff"
                    : msg.isError
                      ? "#b91c1c"
                      : "#111827",
                  padding: "8px 12px",
                  borderRadius: msg.fromMe
                    ? "12px 12px 2px 12px"
                    : "12px 12px 12px 2px",
                  fontSize: 13,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                }}>
                {msg.text}
                {msg.pendingAction && renderAcao(msg.pendingAction)}
              </div>
            ))}
            {aguardando && (
              <div
                style={{
                  alignSelf: "flex-start",
                  background: "#f3f4f6",
                  padding: "8px 12px",
                  borderRadius: "12px 12px 12px 2px",
                  fontSize: 13,
                  color: "#6b7280",
                }}>
                Digitando...
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div
            style={{
              padding: "12px 16px",
              borderTop: "1px solid #f0f0f0",
              display: "flex",
              gap: 8,
            }}>
            <Input
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite sua pergunta..."
              disabled={aguardando || branchId === null || indisponivel}
              maxLength={4000}
              style={{ flex: 1, borderRadius: 20 }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={sendMessage}
              disabled={
                aguardando ||
                !inputText.trim() ||
                branchId === null ||
                indisponivel
              }
              style={{ borderRadius: 20 }}
            />
          </div>
        </div>
      )}
      <Button
        shape="circle"
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          width: 52,
          height: 52,
          background: "#1a1a1a",
          color: "#fff",
          border: "none",
          fontWeight: 700,
          fontSize: 14,
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        }}>
        AI
      </Button>
    </div>
  );
}
